import { useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"

import { PsyProfile } from "modules/PsyProfile/PsyProfile"
import { PsyPageHeader } from "modules/PsyProfile/components"
import { Spinner } from "components/Spinner"
import { Psychologist } from "utils/interfaces"
import { CreatePortalAfterComponent, Footer, Layout } from "components"
import { httpClient } from "utils/infrastructure/httpClient"
import { AFTER_FOOTER } from "components/Footer/components/AfterFooter/constants"

interface Props {
    onError: () => void
}

function generatePsychologistUrl(
    id: string | undefined,
    filter: string | undefined,
    slug: string | undefined
) {
    if (id) {
        return `psychologist/${id}`
    }
    if (filter && slug) {
        return `psychologist/${filter}/${slug}`
    }
    return undefined
}

export function PsyProfileClient({ onError }: Props) {
    const navigate = useNavigate()
    const skipLoading = useRef<boolean>(false)
    const { slug, filter, id } = useParams<Partial<{ slug: string; filter: string; id: string }>>()
    const url = generatePsychologistUrl(id, filter, slug)

    const { isError, isLoading, isSuccess, data } = useQuery(
        ["profile", filter, slug],
        async () => {
            return await httpClient.get<Psychologist>(url || "")
        },
        {
            enabled: !!url && !skipLoading.current,
        }
    )

    useEffect(() => {
        if (isError) {
            onError()
        }
    }, [isError, onError])

    useEffect(() => {
        if (isSuccess && data && id) {
            skipLoading.current = true
            navigate(data.profile_url)
        }
    }, [isSuccess, data, id, navigate])

    useEffect(() => {
        if (!id && slug && filter) {
            skipLoading.current = false
        }
    }, [id, slug, filter])

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
    }, [slug])

    return (
        <Layout className="profile">
            <CreatePortalAfterComponent idPortalName={AFTER_FOOTER}>
                <PsyPageHeader />
                {isLoading && <Spinner />}
                {isSuccess && data && <PsyProfile psy={data} showFooter />}
                {isLoading && <Footer />}
            </CreatePortalAfterComponent>
        </Layout>
    )
}
